import React, { useEffect, useState, useContext } from "react";
import { AutoComplete, type Options, type Option } from "./AutoComplete";
import { Context, getSearchResults } from "services";
import { useDebouncedCallback } from "use-debounce";

interface SearchInputProps {
  placeholder?: string;
  prefix: any;
  suffix: any;
  className?: string;
}

const bankName = process.env.NEXT_PUBLIC_BANK;

export function SearchInput(props: SearchInputProps) {
  const { searchForm }: any = useContext(Context);

  const [isLoading, setLoading] = useState(false);
  const [isDisabled, setDisbled] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [value, setValue] = useState<Option>();
  const [options, setOptions] = useState<Options[]>([]);

  const filterData = (dataToFilter: any) => {
    return (dataToFilter || []).map((data: any) => {
      return {
        label: data?.parent?.search_title,
        value: data?.parent?.search_url,
        category: data?.parent?.asset?.asset_type_name,
      };
    });
  };

  const init = async (): Promise<Options[]> => {
    const initialData: any = await getSearchResults({
      searches: [
        {
          collection: "properties",
          q: "*",
          query_by: "dump.locality.search_title",
          filter_by: `status:active && dump.asset.asset_type_slug:${searchForm?.value?.asset_type?.value} && dump.location.city_name:${searchForm.value.city.label}`,
          filter: "true",
          facet_by: "dump.locality.search_title",
          facet_return_parent: "dump.locality.search_title",
          max_facet_values: "10000",
          per_page: 0,
        },
      ],
    });

    const processedResults = initialData?.results?.map((result: any) => {
      return {
        group: "popular",
        data: filterData(result?.facet_counts?.[0]?.counts),
      };
    });

    if (processedResults) {
      return processedResults; // Return the data based on the conditions
    }
    return [];
  };

  const handleSearch = async (searchTerm: string): Promise<Options[]> => {
    if (searchTerm === "") {
      const data = await init();
      return data;
    }

    const searchedData: any = await getSearchResults({
      searches: [
        {
          collection: "properties",
          q: searchTerm,
          query_by: "dump.locality.search_title",
          filter_by: `status:active && dump.asset.asset_type_slug:${searchForm?.value?.asset_type?.value} && dump.location.city_name:${searchForm.value.city.label} && dump.bank.bank_slug:${bankName}`,
          filter: "true",
          facet_by: "dump.locality.search_title", //dump.location.locality_name
          facet_return_parent: "dump.locality.search_title",
          max_facet_values: "10000",
          per_page: 0,
        },
        {
          collection: "properties",
          q: "*",
          query_by: "dump.locality.search_title",
          filter_by: `status:active && dump.asset.asset_type_slug:${searchForm?.value?.asset_type?.value} && dump.location.city_name:${searchForm.value.city.label}`,
          filter: "true",
          facet_by: "dump.locality.search_title",
          facet_return_parent: "dump.locality.search_title",
          max_facet_values: "10000",
          per_page: 0,
        },
        {
          collection: "properties",
          q: searchTerm,
          query_by: "dump.locality.search_title",
          filter_by: `status:active && dump.location.city_name:${searchForm?.value?.city?.label}`,
          filter: "true",
          facet_by: "dump.locality.search_title", //dump.location.locality_name
          facet_return_parent: "dump.locality.search_title",
          max_facet_values: "10000",
          per_page: 0,
        },
      ],
    });

    const processedResults = searchedData?.results
      .map((result: any, index: number) => {
        if (index === 0) {
          return {
            group: "user_query",
            data: filterData(result?.facet_counts?.[0]?.counts),
          };
        } else if (index === 1) {
          return [
            {
              group: "not_found",
              data: filterData(result?.facet_counts?.[0]?.counts),
            },
            {
              group: "other_results_in_same_asset_type",
              data: filterData(result?.facet_counts?.[0]?.counts),
            },
          ];
        } else if (index === 2) {
          return {
            group: "other_results_in_diff_asset_type",
            data: filterData(result?.facet_counts?.[0]?.counts),
          };
        }
      })
      .flat();

    // console.log("location::>", searchedData, processedResults);

    if (processedResults) {
      const finalResult =
        processedResults.find(
          (result: any) =>
            result?.group === "user_query" && result?.data?.length > 0,
        ) ||
        processedResults.find(
          (result: any) =>
            result?.group === "other_results_in_diff_asset_type" &&
            result?.data?.length > 0,
        ) ||
        processedResults.find(
          (result: any) => result?.group === "not_found", //recommendations
        );

      if (finalResult) {
        if (finalResult?.group === "other_results_in_diff_asset_type") {
          return [
            finalResult,
            processedResults.find(
              (result: any) =>
                result?.group === "other_results_in_same_asset_type" &&
                result?.data?.length > 0,
            ),
          ];
        }

        return [finalResult]; // Return the data based on the conditions
      }
    }
    return [];
  };

  const handleOnSearchChange = useDebouncedCallback(async (e: string) => {
    const data = await handleSearch(e);
    setOptions(data);
  }, 300);

  useEffect(() => {
    (async () => {
      await handleOnSearchChange(inputValue);
    })();
  }, [inputValue]);

  useEffect(() => {
    (async () => {
      const data = await init();
      setOptions(data);
    })();
  }, [searchForm?.value?.asset_type, searchForm?.value?.city]);

  return (
    <div
      className={`w-full flex justify-between gap-x-2 items-center border-input lg:border-t h-[58px] lg:h-[63px]`}
    >
      {props.prefix && (
        <div className="flex items-center">
          {props?.prefix}
          <div className={"border-l border-brand-borderGreyTwo h-12"} />
        </div>
      )}
      <AutoComplete
        options={options}
        emptyMessage="No results."
        placeholder={props?.placeholder}
        className="flex-1 w-full ring-none outline-none focus:ring-0 focus:outline-none border-none focus:border-none mx-auto"
        isLoading={isLoading}
        onValueChange={setValue}
        onInputValueChange={(value: any) => {
          setInputValue(value);
        }}
        inputValue={inputValue}
        value={value}
        city={searchForm.getField("city")}
        disabled={isDisabled}
      />

      {props.suffix && <div>{props?.suffix}</div>}
    </div>
  );
}
